import { DeleteConfirmationDialog } from '@components/dialogs/DeleteConfirmationDialog'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Tooltip,
  debounce,
} from '@mui/material'
import {
  DataGridPro,
  GridColumns,
  GridRowId,
  GridToolbarContainer,
} from '@mui/x-data-grid-pro'
import { useSelector } from '@state/store'
import {
  setMedicalPage,
  setMedicalLimit,
  setMedicalSearch,
  setMedicalTypeFilter,
  clearMedicalFilters,
} from '@state/reducers/medicalEquipmentReducer'
import {
  findAllMedicalEquipment,
  deleteMedicalEquipment,
  deleteBulkMedicalEquipment,
} from '@state/thunks/medicalEquipmentThunk'
import { useCallback, useEffect, useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { MedicalEquipmentForm } from './MedicalEquipmentForm'
import { EditIcon, Trash2 } from 'lucide-react'
import { MedicalEquipment, ModalityEnum } from '@services/api'
import { Add, Refresh, Delete } from '@mui/icons-material'
import moment from 'moment'
import { useIntl } from 'react-intl'

function CustomToolbar({
  onAdd,
  onRefresh,
  onDelete,
  selectedCount,
}: {
  onAdd: () => void
  onRefresh: () => void
  onDelete: () => void
  selectedCount: number
}) {
  const intl = useIntl()

  return (
    <GridToolbarContainer
      sx={{
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Typography variant="h6" sx={{ mr: 2 }}>
          {intl.formatMessage({ id: 'medicalEquipments' })}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        {selectedCount > 0 && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={onDelete}
            size="small"
          >
            Delete ({selectedCount})
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={onRefresh}
          size="small"
        >
          {intl.formatMessage({ id: 'refresh' })}
        </Button>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={onAdd}
          size="small"
        >
          {intl.formatMessage({ id: 'add-medical-equipment' })}
        </Button>
      </Box>
    </GridToolbarContainer>
  )
}

export default function Equipments() {
  const dispatch = useDispatch()
  const intl = useIntl()
  const [openAddDialog, setOpenAddDialog] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([])
  const [medicalEquipment, setMedicalEquipment] = useState<
    MedicalEquipment | undefined
  >(undefined)

  const handleCloseAddDialog = useCallback(() => {
    setOpenAddDialog(false)
    setMedicalEquipment(undefined)
  }, [])

  const { data, pagination, filters, loading } = useSelector(
    (state) => state.medicalEquipment,
  )

  const [searchInput, setSearchInput] = useState('')

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        dispatch(setMedicalSearch(value))
      }, 500),
    [dispatch],
  )
  useEffect(() => {
    setSearchInput(filters.search)
  }, [filters.search])

  useEffect(() => {
    return () => {
      debouncedSearch.clear()
    }
  }, [debouncedSearch])

  const columns: GridColumns = [
    {
      field: 'code',
      headerName: intl.formatMessage({ id: 'code' }),
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'serialNumber',
      headerName: intl.formatMessage({ id: 'serialNumber' }),
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'model',
      headerName: intl.formatMessage({ id: 'model' }),
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'commissioningDate',
      headerName: intl.formatMessage({ id: 'commissioningDate' }),
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => {
        return params.row.commissioningDate
          ? moment(params.row.commissioningDate).format('DD/MM/YYYY')
          : '-'
      },
    },
    {
      field: 'site',
      headerName: intl.formatMessage({ id: 'site' }),
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.site?.label || '-',
    },
    {
      field: 'examRoom',
      headerName: intl.formatMessage({ id: 'examRoom' }),
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.examRoom?.label || '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                setMedicalEquipment(params.row)
                setOpenAddDialog(true)
              }}
            >
              <EditIcon size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteSingle(params.row.id)}
            >
              <Trash2 size={18} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  useEffect(() => {
    dispatch(
      findAllMedicalEquipment({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search || undefined,
        type: filters.type || undefined,
      }),
    )
  }, [
    dispatch,
    pagination.page,
    pagination.limit,
    filters.search,
    filters.type,
  ])

  const onRefresh = useCallback(() => {
    dispatch(
      findAllMedicalEquipment({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search || undefined,
        type: filters.type || undefined,
      }),
    )
  }, [
    dispatch,
    pagination.page,
    pagination.limit,
    filters.search,
    filters.type,
  ])

  const handleDeleteSingle = async (id: number) => {
    dispatch(deleteMedicalEquipment(id))
    onRefresh()
  }

  const _onSelectionChange = (selection: GridRowId[]) => {
    setSelectedRows(selection)
  }

  const _onDeleteConfirm = async () => {
    dispatch(deleteBulkMedicalEquipment(selectedRows as number[]))
    setIsDeleteDialogOpen(false)
    setSelectedRows([])
    onRefresh()
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchInput(value)
    debouncedSearch(value)
  }

  const handleTypeFilterChange = (event: any) => {
    dispatch(setMedicalTypeFilter(event.target.value))
  }

  const typeOptions = Object.values(ModalityEnum).map((value) => ({
    label: value,
    value,
  }))

  return (
    <Box
      sx={{
        height: 'calc(100vh - 130px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Paper
        elevation={1}
        sx={{
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <TextField
            label={intl.formatMessage({
              id: 'searchByCodeOrSerialNumber',
            })}
            variant="outlined"
            value={searchInput}
            onChange={handleSearchChange}
            size="small"
            sx={{ minWidth: 400 }}
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filters.type}
              onChange={handleTypeFilterChange}
              label="Type"
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 400,
                  },
                },
              }}
            >
              {typeOptions.map((tp, i) => (
                <MenuItem key={`${tp.value}/${i}`} value={tp.value}>
                  {tp.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            onClick={() => {
              setSearchInput('')
              debouncedSearch.clear()
              dispatch(clearMedicalFilters())
            }}
            size="small"
          >
            {intl.formatMessage({ id: 'clearFilters' })}
          </Button>

          <Box sx={{ ml: 'auto' }}>
            <Typography variant="body2" color="text.secondary">
              Total: {pagination.total} equipment(s)
            </Typography>
          </Box>
        </Box>
      </Paper>
      <Paper
        elevation={1}
        sx={{
          flex: 1,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataGridPro
          rows={data}
          columns={columns}
          loading={loading}
          checkboxSelection
          disableSelectionOnClick
          pagination
          paginationMode="server"
          page={pagination.page - 1}
          pageSize={pagination.limit}
          rowCount={pagination.total}
          onPageChange={(newPage) => dispatch(setMedicalPage(newPage + 1))}
          onPageSizeChange={(newPageSize) =>
            dispatch(setMedicalLimit(newPageSize))
          }
          rowsPerPageOptions={[10, 20, 50, 100]}
          onSelectionModelChange={_onSelectionChange}
          selectionModel={selectedRows}
          components={{
            Toolbar: CustomToolbar,
          }}
          componentsProps={{
            toolbar: {
              onAdd: () => {
                setMedicalEquipment(undefined)
                setOpenAddDialog(true)
              },
              onRefresh,
              onDelete: () => {
                setIsDeleteDialogOpen(true)
              },
              selectedCount: selectedRows.length,
            },
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        />
      </Paper>

      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="lg"
        fullWidth
        aria-labelledby="form-dialog-title"
        scroll="paper"
      >
        <DialogTitle>
          {medicalEquipment
            ? intl.formatMessage({ id: 'edit-medical-equipment' })
            : intl.formatMessage({ id: 'add-medical-equipment' })}
        </DialogTitle>
        <DialogContent>
          <MedicalEquipmentForm
            onClose={() => {
              handleCloseAddDialog()
              onRefresh()
            }}
            row={medicalEquipment}
          />
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={_onDeleteConfirm}
        title="Confirmer la suppression"
        description={`Voulez-vous vraiment supprimer ${selectedRows.length} équipement(s) médical(aux) sélectionné(s) ?`}
      />
    </Box>
  )
}
